import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, Modal, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { ADD_ROLE } from '../graphql/mutations/addRole';
import { UPDATE_ROLE } from '../graphql/mutations/updateRole';
import { DELETE_ROLE } from '../graphql/mutations/deleteRole';
import { GET_ROLE_DETAIL_BY_ID } from '../graphql/queries/getRoleDetailById';
import { QUERY_PERMISSION_PACKAGES_FOR_SELECT } from '../graphql/queries/queryPermissionPackagesForSelect';
import type { RoleType } from '../graphql/queries/queryRoleByPage';
import type { RoleApiRequestInput } from '../graphql/mutations/addRole';
import type { UpdateRoleInput } from '../graphql/mutations/updateRole';
import { getPlatformPermissionErrorMessage } from '../../utils/errorCode';
import { HasResource } from '../../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

type DrawerMode = 'create' | 'edit';

export interface RoleDrawerProps {
  visible: boolean;
  mode: DrawerMode;
  onClose: () => void;
  onSuccess: () => void;
  editRecord: RoleType | null;
}

/** 多选下拉的 filterSort：把已选中的选项排到前面，未选中的保持原有相对顺序 */
const sortSelectedFirst = (selectedValues: number[] | undefined) =>
  (a: { value: number }, b: { value: number }) => {
    const aSelected = selectedValues?.includes(a.value) ? 0 : 1;
    const bSelected = selectedValues?.includes(b.value) ? 0 : 1;
    return aSelected - bSelected;
  };

const RoleDrawer: React.FC<RoleDrawerProps> = ({ visible, mode, onClose, onSuccess, editRecord }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [addRole, { loading: adding }] = useMutation(ADD_ROLE);
  const [updateRole, { loading: updating }] = useMutation(UPDATE_ROLE);
  const [deleteRole, { loading: deleting }] = useMutation(DELETE_ROLE);

  const [loadRoleDetail, { data: detailData }] = useLazyQuery(GET_ROLE_DETAIL_BY_ID, {
    fetchPolicy: 'network-only',
  });

  const { data: pkgData } = useQuery(QUERY_PERMISSION_PACKAGES_FOR_SELECT, {
    skip: !visible,
    fetchPolicy: 'network-only',
  });

  const packageOptions =
    pkgData?.listAllPermissionPackages?.map((p: { id: number | string; name: string }) => ({
      value: Number(p.id),
      label: `${p.name} (${p.id})`,
    })) ?? [];

  // 已选中的权限包排到下拉列表最前面，方便再次打开时快速找到
  const packageIdsValue = Form.useWatch('packageIds', form) as number[] | undefined;

  // 下拉框每次关闭后换 key 强制重新挂载，保证下次打开时选项列表回到顶部（而不是停留在上次滚动位置）
  const [packageSelectKey, setPackageSelectKey] = useState(0);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setDeleteModalOpen(false);
      return;
    }

    if (mode === 'create') {
      form.resetFields();
      form.setFieldsValue({
        id: undefined,
        name: '',
        description: '',
        packageIds: [],
      });
      return;
    }

    if (mode === 'edit' && editRecord) {
      form.resetFields();
      void loadRoleDetail({ variables: { id: Number(editRecord.id) } });
    }
  }, [visible, mode, editRecord, form, loadRoleDetail]);

  useEffect(() => {
    if (!visible || mode !== 'edit') return;
    const d = detailData?.getRoleDetailById?.data;
    if (!d) return;
    form.setFieldsValue({
      id: Number(d.id),
      name: d.name ?? '',
      description: d.description ?? '',
      packageIds: (d.packageIds ?? []).map((x: number) => Number(x)),
    });
  }, [visible, mode, detailData, form]);

  const handleClose = () => {
    form.resetFields();
    setDeleteModalOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'edit' && editRecord) {
        const input: UpdateRoleInput = {
          id: Number(values.id),
          name: String(values.name).trim(),
          description: String(values.description ?? '').trim(),
          packageIds: (values.packageIds as number[]) ?? [],
        };

        const res = await updateRole({ variables: { input } });
        const result = res.data?.updateRole;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      } else {
        const input: RoleApiRequestInput = {
          name: String(values.name).trim(),
          description: String(values.description ?? '').trim(),
          packageIds: (values.packageIds as number[]) ?? [],
        };

        const res = await addRole({ variables: { input } });
        const result = res.data?.addRole;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  const handleDelete = () => {
    if (mode !== 'edit' || !editRecord?.id) return;
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (mode !== 'edit' || !editRecord?.id) return;
    try {
      const res = await deleteRole({ variables: { input: { id: Number(editRecord.id) } } });
      const result = res.data?.deleteRole;
      if (result?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.success(toastText);
        setDeleteModalOpen(false);
        onSuccess();
        onClose();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  return (
    <Drawer
      title={mode === 'edit' ? t('central.platformPermission.role.drawer.edit') : t('central.platformPermission.role.drawer.add')}
      open={visible}
      onClose={handleClose}
      destroyOnClose
      width={520}
      footer={
        <div className="flex justify-between gap-2">
          <div>
            {mode === 'edit' && (
              <HasResource resourceCodes={[ResourceCode.role.DELETE]}>
                <Button
                  loading={deleting}
                  onClick={handleDelete}
                  className="bg-red-500 border-red-500 text-white hover:!bg-red-500 hover:!border-red-500 hover:!text-white"
                >
                  {t('components.button.delete') as string}
                </Button>
              </HasResource>
            )}
          </div>
          <div className="flex gap-2">
            <HasResource resourceCodes={[ResourceCode.role.ADD, ResourceCode.role.UPDATE]}>
              <Button
                type="primary"
                loading={adding || updating}
                onClick={handleSubmit}
              >
                {t('components.button.confirm') as string}
              </Button>
            </HasResource>
            <Button onClick={onClose}>
              {t('components.button.cancel') as string}
            </Button>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        labelCol={{ flex: '160px' }}
        wrapperCol={{ flex: 'auto' }}
        labelAlign="left"
        labelWrap
        colon
      >
        {mode === 'edit' && (
          <Form.Item name="id" label="ID">
            <Input disabled />
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label={t('central.platformPermission.role.fields.name')}
          rules={[{ required: true, message: t('central.platformPermission.role.validation.nameRequired') as string }]}
        >
          <Input maxLength={100} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item name="description" label={t('central.platformPermission.role.fields.description')}>
          <Input.TextArea rows={2} maxLength={500} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item
          name="packageIds"
          label={t('central.platformPermission.role.fields.packageIds')}
          required
          rules={[
            {
              validator: async (_, value: number[] | null | undefined) => {
                if (Array.isArray(value) && value.length > 0) return;
                throw new Error(t('central.platformPermission.errorCodes.PACKAGE_ID_REQUIRED') as string);
              },
            },
          ]}
        >
          <Select
            key={packageSelectKey}
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            autoClearSearchValue={false}
            filterSort={sortSelectedFirst(packageIdsValue)}
            maxTagCount="responsive"
            onOpenChange={(open) => { if (!open) setPackageSelectKey((k) => k + 1); }}
            placeholder={t('components.input.select') as string}
            options={packageOptions}
          />
        </Form.Item>
      </Form>
      <Modal
        title={t('central.platformPermission.role.deleteConfirmTitle') as string}
        open={deleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText={t('components.button.confirm') as string}
        cancelText={t('components.button.cancel') as string}
        okButtonProps={{
          loading: deleting,
          className: 'bg-purple-600 border-purple-600 hover:!bg-purple-700 hover:!border-purple-700',
        }}
      >
        <p>{t('central.platformPermission.role.deleteConfirmContent') as string}</p>
      </Modal>
    </Drawer>
  );
};

export default RoleDrawer;
